import type { ProductSetContentDM } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/MultiSectionView.types';
import { getViewData } from '@Components/BigBrowse/Views/DetailViews/SingleSectionView/getViewData';

import type { GridCard } from '../../../Model/Classes/GridCard';
import { getCard } from '../../../Model/Classes/GridCard';
import type {
  CameraProps,
  ColumnConfig,
  GridCardView,
  PlaneBox,
  PlaneSize,
  Position2D,
} from '../../../Model/ModelTypes';
import { PlaneTypes } from '../../../Model/ModelTypes';
import { NAV_BUTTON_SIZE } from '../BBToolbar/BBToolbarConstants';

const HERO_CARD_SIZE = { width: 590, height: 590 };
const PRODUCT_CARD_SIZE = { width: 265, height: 265 };
const HALF_HERO_SIZE = {
  width: PRODUCT_CARD_SIZE.width,
  height: HERO_CARD_SIZE.height,
};

const DESCRIPTION_BOX_SIZE = {
  width: HERO_CARD_SIZE.width,
  height: 47 + 15 + 104,
};
const SUBTITLE_BOX_SIZE = { width: 244, height: 24 };

const TINY_GAP_SIZE = { width: PRODUCT_CARD_SIZE.width, height: 10 };
const SMALL_GAP_SIZE = { width: PRODUCT_CARD_SIZE.width, height: 35 };
const LARGE_GAP_SIZE = { width: PRODUCT_CARD_SIZE.width, height: 70 };

const COLUMN_GAP = 60;
const TOP_PADDING = 54;

type SingleSectionLayoutRenderer = (params: {
  data: ProductSetContentDM;
  cameraBox: PlaneBox;
}) => {
  gridSizePx: PlaneSize;
  items: GridCard<PlaneTypes>[];
  scrollBox?: PlaneBox;
};

/** Allocate items to a rectangle plane (Grid), based on the 'Detail View' layout.
 * Returns the items with their position relative to the Grid's center plus the Grid size
 */
export const getSingleSectionLeftGridLayout: SingleSectionLayoutRenderer = ({
  data,
  cameraBox,
}) => {
  const viewData = getViewData(data);

  if (viewData.length === 0) {
    return { gridSizePx: { width: 0, height: 0 }, items: [] };
  }

  const { left, right } = cameraBox;
  const widthPx = right - left;

  // Calculate the number of columns
  const { numOfColumns, reminderPx } = getNumOfColumns(widthPx);

  // Prepare Layout array to distribute items
  let grid: GridCard<PlaneTypes>[][] = new Array(numOfColumns)
    .fill(0)
    .map(() => []);
  let unallocatedIndex = 0;

  // Populate Grid
  ({ unallocatedIndex, grid } = addHeadImage({
    grid,
    items: viewData,
    index: unallocatedIndex,
    numOfColumns,
  }));

  grid = addGapsInFirstLine(grid);

  ({ unallocatedIndex, grid } = allocateFirstTwoLines(
    grid,
    viewData,
    unallocatedIndex
  ));

  if (unallocatedIndex < viewData.length) {
    grid = allocateRestOfLines(grid, viewData, unallocatedIndex);
  }

  grid = assignBox(grid, reminderPx / 2);
  const gridHeight = getGridHeight(grid, TOP_PADDING);
  const gridSizePx = { width: widthPx, height: gridHeight };
  const cardsOnly = grid.flat(1).filter((item) => item.type !== PlaneTypes.Gap);

  assignPositions(cardsOnly, gridSizePx, cameraBox);

  const scrollBox: PlaneBox = {
    left,
    right,
    top: cameraBox.top,
    bottom: cameraBox.top - gridSizePx.height,
  };

  cardsOnly.forEach((card) => (card.key = card.data?.key));

  return { gridSizePx, items: cardsOnly, scrollBox };
};

type AddHeadImageParams = {
  grid: GridCard<PlaneTypes>[][];
  items: GridCardView[];
  index: number;
  numOfColumns: number;
};
const addHeadImage = ({
  grid,
  items,
  index,
  numOfColumns,
}: AddHeadImageParams): {
  grid: GridCard<PlaneTypes>[][];
  unallocatedIndex: number;
} => {
  const result = grid.slice();

  // add an initial gap on all lines
  for (let i = 0; i < numOfColumns; i++) {
    result[i].push(
      getCard({
        type: PlaneTypes.Gap,
        size: { width: PRODUCT_CARD_SIZE.width, height: TOP_PADDING },
      })
    );
  }

  const cardSize = numOfColumns >= 2 ? HERO_CARD_SIZE : PRODUCT_CARD_SIZE;
  // Add Hero Card
  const headImage: GridCard<PlaneTypes> = getCard({
    data: { isViewHero: true, ...items[0] },
    size: cardSize,
    type: PlaneTypes.Card_SingleSectionView_Hero,
  });

  const textBoxSize =
    numOfColumns >= 2
      ? DESCRIPTION_BOX_SIZE
      : { width: PRODUCT_CARD_SIZE.width, height: DESCRIPTION_BOX_SIZE.height };

  result[0].push(
    headImage,
    DETAIL_VIEW_GAP_HORIZONTAL_LARGE(),
    getCard({
      data: items[1], // this is the text view
      type: PlaneTypes.Card_SingleSectionView_Description,
      size: textBoxSize,
    }),
    DETAIL_VIEW_GAP_HORIZONTAL_LARGE()
  );
  result[1]?.push(
    getCard({
      type: PlaneTypes.Gap,
      size: HALF_HERO_SIZE,
    }),
    DETAIL_VIEW_GAP_HORIZONTAL_LARGE(),
    getCard({
      type: PlaneTypes.Gap,
      size: {
        width: PRODUCT_CARD_SIZE.width,
        height: DESCRIPTION_BOX_SIZE.height,
      },
    }),
    DETAIL_VIEW_GAP_HORIZONTAL_LARGE(),
    DETAIL_VIEW_GAP_HORIZONTAL_SMALL()
  );

  return { unallocatedIndex: index + 2, grid: result };
};

const addGapsInFirstLine = (
  grid: GridCard<PlaneTypes>[][]
): GridCard<PlaneTypes>[][] => {
  const result = grid.slice();

  result.forEach((column, x) => {
    if (x > 2 && x % 2 !== 0) {
      column.push(DETAIL_VIEW_GAP_HORIZONTAL_SMALL());
    }
  });

  return result;
};

const allocateFirstTwoLines = (
  grid: GridCard<PlaneTypes>[][],
  items: GridCardView[],
  index: number
): { unallocatedIndex: number; grid: GridCard<PlaneTypes>[][] } => {
  const result = grid.slice();
  let unallocatedIndex = index;

  for (let i = 0; i < 2; i++) {
    // eslint-disable-next-line @typescript-eslint/no-loop-func
    result.forEach((column, x) => {
      if (x >= 2 && unallocatedIndex < items.length) {
        const data = items[unallocatedIndex];

        column.push(...getProductCard(data));
        unallocatedIndex++;
      }
    });
  }

  return { unallocatedIndex, grid: result };
};

const allocateRestOfLines = (
  grid: GridCard<PlaneTypes>[][],
  items: GridCardView[],
  index: number
): GridCard<PlaneTypes>[][] => {
  const result = grid.slice();
  let unallocatedIndex = index;
  let x = 0;

  while (unallocatedIndex < items.length) {
    const data = items[unallocatedIndex];
    result[x].push(...getProductCard(data));
    unallocatedIndex++;
    x = x === grid.length - 1 ? 0 : x + 1;
  }

  return result;
};

const assignBox = (
  grid: GridCard<PlaneTypes>[][],
  leftGap: number
): GridCard<PlaneTypes>[][] => {
  // Assuming TopLeft of grids is (0,0).
  // x > 0 left -> right
  // y > 0 bottom -> top
  // Calculate Coordinates per item
  const result = grid.slice();

  // iterate columns
  for (let x = 0; x < result.length; x++) {
    const column = result[x];
    const columnLeft = x * (PRODUCT_CARD_SIZE.width + COLUMN_GAP) + leftGap;

    // iterate items in each column
    for (let index = 0; index < column.length; index++) {
      const top = index === 0 ? 0 : result[x][index - 1].box!.bottom;

      result[x][index].setTopLeft({ x: columnLeft, y: top });
    }
  }
  return result;
};

const assignPositions = (
  grid: GridCard<PlaneTypes>[],
  gridSizePx: PlaneSize,
  camera: PlaneBox
) => {
  const { dx, dy } = { dx: gridSizePx.width / 2, dy: gridSizePx.height / 2 };
  const { top, left } = camera;
  const { width, height } = gridSizePx;

  const gridPosition: Position2D = {
    x: left + width / 2,
    y: top - height / 2,
  };

  grid.forEach((item) => {
    item.shift({ x: -dx, y: dy }); // position cards relative to grid center
    item.setParent({ worldPosition: gridPosition }); // position cards relative to world
  });

  // because of a tiny inconsistency between 'getHeroPosition' and Layout calculation of hero position
  // we sync hero's position with the 'getHeroPosition' result
  const hero = grid.find(
    (card) => card.type === PlaneTypes.Card_SingleSectionView_Hero
  );
  hero?.setParent(undefined);
  hero?.setPosition(getHeroPosition(camera));
};

const getGridHeight = (
  grid: GridCard<PlaneTypes>[][],
  topPadding: number = 0
): number => {
  // find the lowest bottom coord of columns
  const bottomOfGrid = -Math.min(
    ...grid
      .filter((column) => Boolean(column.length))
      .map((column) => column[column.length - 1].box.bottom ?? 0)
  );

  return bottomOfGrid + topPadding + NAV_BUTTON_SIZE.height;
};

/** Return the number of columns for the Grid, based on its width and simple square widths */
export const getNumOfColumns = (
  widthPx: number,
  cardSize: number = PRODUCT_CARD_SIZE.width,
  gapSize: number = COLUMN_GAP
): ColumnConfig => {
  const columnWidth = cardSize + gapSize;

  const colDiv = Math.floor(widthPx / columnWidth);
  const reminder = widthPx % columnWidth;

  const numOfColumns =
    reminder >= cardSize
      ? colDiv + 1 // if the reminder is the vertical gap at the end of grid, we can ignore it
      : colDiv;

  const effectiveWidth = numOfColumns * cardSize + (numOfColumns - 1) * gapSize;

  return { numOfColumns, reminderPx: widthPx - effectiveWidth };
};

export const getSingleSectionHeroSpecs = (
  camera: CameraProps
): { worldPosition: Position2D; size: PlaneSize } => {
  const worldPosition = getHeroPosition(camera.box);
  const size = getHeroSize(camera.box);

  return { worldPosition, size };
};

const getHeroSize = (cameraBox: PlaneBox): PlaneSize => {
  const { left, right } = cameraBox;
  const { numOfColumns } = getNumOfColumns(right - left);

  return numOfColumns >= 2 ? HERO_CARD_SIZE : PRODUCT_CARD_SIZE;
};

const getHeroPosition = (cameraBox: PlaneBox): Position2D => {
  const { left, right, top } = cameraBox;

  const { reminderPx } = getNumOfColumns(right - left);

  const size = getHeroSize(cameraBox);
  return {
    x: left + (size.width / 2 + reminderPx / 2),
    y: top - (size.height / 2 + TOP_PADDING),
  };
};

const getProductCard = (data: GridCardView): GridCard<PlaneTypes>[] => [
  getCard({
    data,
    type: PlaneTypes.Card_SingleSectionView_Product,
    size: PRODUCT_CARD_SIZE,
  }),
  DETAIL_VIEW_GAP_SUBTITLE(),
  getCard({
    type: PlaneTypes.Card_SingleSectionView_Subtitle,
    data: {
      // @ts-expect-error
      id: data.id,
      key: `${data.id}subtitle`,
      title: data.product?.name ?? '',
    },
    blockHover: true,
    size: SUBTITLE_BOX_SIZE,
  }),
  DETAIL_VIEW_GAP_HORIZONTAL_SMALL(),
];

const DETAIL_VIEW_GAP_HORIZONTAL_LARGE = (): GridCard<PlaneTypes> =>
  getCard({
    type: PlaneTypes.Gap,
    size: LARGE_GAP_SIZE,
  });

const DETAIL_VIEW_GAP_HORIZONTAL_SMALL = (): GridCard<PlaneTypes> =>
  getCard({
    type: PlaneTypes.Gap,
    size: SMALL_GAP_SIZE,
  });

const DETAIL_VIEW_GAP_SUBTITLE = (): GridCard<PlaneTypes> =>
  getCard({
    type: PlaneTypes.Gap,
    size: TINY_GAP_SIZE,
  });
